import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useRightPanel } from './RightPanelContext';
import { useNTTableContext } from '../NTTableContext';
import { useModuleRegistration } from './useModuleRegistration';
import { getModule } from './moduleRegistry';
import { useNTContext } from '../NTContext';
import { useNTUtilsContext } from '../NTUtilsContext';
import ModuleSwitcher from './subcomponents/ModuleSwitcher';
import { useReportsStore } from '../../../../hooks/useReportsStore';
import { useFlags } from 'launchdarkly-react-client-sdk';
import './RightPanelContainer.scss';

interface RightPanelContainerProps {
  className?: string;
  rawData: any;
  aiData?: any;
}

export const RightPanelContainer: React.FC<RightPanelContainerProps> = ({ className, rawData, aiData }) => {
  const {
    getActiveModule,
    activeModuleId,
    setActiveModuleId,
    isPanelVisible,
    setPanelVisible,
    isExpanded,
    setIsExpanded,
    setPanelSize,
    outsideCall,
    setOutsideCall,
  } = useRightPanel();
  const { tableWidth } = useNTTableContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { isExpanded: ctxExpanded, setIsExpanded: setIsExpandedTable } = useNTTableContext();

  // Use the custom hook for module registration
  const { getModuleProps, togglePanel } = useModuleRegistration(aiData, rawData);
  const { actions, setActions } = useNTUtilsContext();
  const ntContext = useNTContext();
  const { aiReport, reportQueue, setReportQueue } = useReportsStore(state => ({
    aiReport: state.aiReport,
    reportQueue: state.reportQueue,
    setReportQueue: state.setReportQueue,
  }));
  const { enableQueueReport } = useFlags();
  const prevReportQueueRef = React.useRef<boolean>(false);

  // Store the last pressed module ID to activate when panel becomes visible
  const [pendingModuleId, setPendingModuleId] = useState<string | null>(null);

  // Map of keyboard shortcuts to module IDs
  // TODO: Refactor to dynamically read from module configs and check if modules are enabled
  const shortcutMap: Record<string, string> = useMemo(
    () => ({
      g: 'chart',
      /*       c: 'comments',
      k: 'ai-analysis',
      t: 'transactions', */
    }),
    []
  );

  // Effect to handle setting the active module when the panel becomes visible
  // TODO: Improve the timing of module activation with panel animation
  // Currently there might be a race condition between panel visibility and module activation
  // A more robust solution would involve tracking animation state or using refs to the panel element
  useEffect(() => {
    if (isPanelVisible && pendingModuleId) {
      // Panel is now visible, set the active module
      setActiveModuleId(pendingModuleId);
      // Clear the pending module ID
      setPendingModuleId(null);
    }
  }, [isPanelVisible, pendingModuleId, setActiveModuleId]);

  // Handle keyboard shortcuts via custom events
  useEffect(() => {
    // This handler processes the custom events from NTSelectionContext
    const handleModuleShortcut = (e: CustomEvent) => {
      const { key, moduleId } = e.detail;

      // Get the current active module
      const activeModule = getActiveModule();

      // If the panel is open and this module is already active, close the panel
      if (isPanelVisible && activeModule && activeModule.id === moduleId) {
        setPanelVisible(false);
        // Also update the NTUtils context to keep them in sync
        setActions({ ...actions, detailsPanel: false });
        return;
      }

      if (isPanelVisible) {
        // If panel is already visible, switch module immediately
        setActiveModuleId(moduleId);
      } else {
        // If panel is not visible yet, store the module ID to be set when panel becomes visible
        setPendingModuleId(moduleId);
      }
    };

    // Handle direct keyboard events as a fallback
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't process shortcuts if during text input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Handle expansion toggle
      if (e.key === 'e') {
        if (isPanelVisible) {
          e.preventDefault();
          const newState = !isExpanded;
          setIsExpanded(newState);
          setIsExpandedTable(newState);
          return;
        }
      }

      const pressedModuleId = shortcutMap[e.key];
      if (!pressedModuleId) return;

      // Get the current active module
      const activeModule = getActiveModule();

      // If the panel is open and this module is already active, close the panel
      if (isPanelVisible && activeModule && activeModule.id === pressedModuleId) {
        setPanelVisible(false);
        // Also update the NTUtils context to keep them in sync
        setActions({ ...actions, detailsPanel: false });
        return;
      }

      if (isPanelVisible) {
        // If panel is already visible, switch module immediately
        setActiveModuleId(pressedModuleId);
      } else {
        // If panel is not visible yet, store the module ID to be set when panel becomes visible
        setPendingModuleId(pressedModuleId);
        // Make sure the panel is visible
        setPanelVisible(true);
      }
    };

    // Listen for both custom events and keyboard events
    document.addEventListener('module-shortcut', handleModuleShortcut as EventListener);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('module-shortcut', handleModuleShortcut as EventListener);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isPanelVisible,
    activeModuleId,
    setIsExpandedTable,
    setPanelVisible,
    getActiveModule,
    setActiveModuleId,
    shortcutMap,
    actions,
    setActions,
  ]);

  // Auto-expand first time a module with defaultExpanded is shown
  const autoExpandedRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (!isPanelVisible) return;
    const mod = getModule(activeModuleId || '');
    if (
      mod &&
      mod.expandEnabled &&
      (mod as any).defaultExpanded &&
      !isExpanded &&
      autoExpandedRef.current !== activeModuleId
    ) {
      setIsExpanded(true);
      setIsExpandedTable(true);
      autoExpandedRef.current = activeModuleId || null;
    }
  }, [activeModuleId, isPanelVisible, isExpanded, setIsExpanded, setIsExpandedTable]);

  // NTTableContext is the source of truth for expansion state
  useEffect(() => {
    if (ctxExpanded !== isExpanded) {
      setIsExpanded(ctxExpanded);
    }
  }, [ctxExpanded, isExpanded]);

  // Always reset expanded state when panel is closed
  useEffect(() => {
    if (!isPanelVisible && isExpanded) {
      setIsExpanded(false);
      setIsExpandedTable(false);
    }
  }, [isPanelVisible, isExpanded, setIsExpanded, setIsExpandedTable]);

  // Sync URL param when isExpanded changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const EXPANDED_PARAM = 'rightPanelExpanded';
    if (isExpanded) {
      params.set(EXPANDED_PARAM, '1');
    } else {
      params.delete(EXPANDED_PARAM);
    }
    const newSearch = params.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    if (newPath !== `${location.pathname}${location.search}`) {
      navigate(newPath, { replace: true });
    }
  }, [isExpanded, location.pathname, location.search, navigate]);

  // Sync tableWidth param with current width (left pane)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const WIDTH_PARAM = 'tableWidth';
    params.set(WIDTH_PARAM, tableWidth);
    const newSearch = params.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    if (newPath !== `${location.pathname}${location.search}`) {
      navigate(newPath, { replace: true });
    }
  }, [tableWidth, location.pathname, location.search, navigate]);

  // Sync panelSize param with current width when not expanded
  useEffect(() => {
    if (!isPanelVisible) return;
    if (isExpanded) return; // when expanded, width is full
    let width = 0;
    if (tableWidth.endsWith('%')) {
      const pct = parseFloat(tableWidth);
      if (!isNaN(pct)) {
        width = Math.round(((100 - pct) * window.innerWidth) / 100);
      }
    } else if (tableWidth.endsWith('px')) {
      const left = parseFloat(tableWidth);
      if (!isNaN(left)) {
        width = Math.round(window.innerWidth - left);
      }
    }
    if (width > 0) {
      setPanelSize(width);
    }
  }, [tableWidth, isExpanded, isPanelVisible, setPanelSize]);

  // On mount, read expanded param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('rightPanelExpanded') === '1' && !isExpanded) {
      setIsExpanded(true);
    }
  }, []);

  useEffect(() => {
    const isReportQueueEnabled = Boolean(enableQueueReport);
    const isOutsideCall = aiReport || (reportQueue && isReportQueueEnabled);
    setOutsideCall(isOutsideCall);

    if (reportQueue && !isReportQueueEnabled) {
      setReportQueue(false);
      return;
    }

    if (reportQueue) {
      // Make sure the panel is visible
      setPanelVisible(true);
      // Only force module switch on false → true transition
      if (!prevReportQueueRef.current) {
        setActiveModuleId('report-queue');
        if (isExpanded) {
          setIsExpanded(false);
          setIsExpandedTable(false);
        }
      }
      prevReportQueueRef.current = reportQueue;
      return;
    }

    if (aiReport) {
      // Make sure the panel is visible
      setPanelVisible(true);
      setActiveModuleId('ai-analysis');
      return;
    }

    if (isPanelVisible) {
      // If panel is already visible, switch module immediately
      setActiveModuleId('chart');
    }
    prevReportQueueRef.current = reportQueue;
  }, [
    aiReport,
    reportQueue,
    isPanelVisible,
    setActiveModuleId,
    setPanelVisible,
    setOutsideCall,
    isExpanded,
    setIsExpanded,
    setIsExpandedTable,
    enableQueueReport,
  ]);

  const handleExpand = () => {
    const activeModule = getActiveModule();
    if (!activeModule || !activeModule.expandEnabled) return;

    const newVal = !isExpanded;
    setIsExpanded(newVal);
    setIsExpandedTable(newVal);
  };

  // Render the active module component
  const renderActiveModule = () => {
    const activeModule = getActiveModule();
    if (!activeModule) return null;

    const ModuleComponent = activeModule.component;
    return <ModuleComponent {...getModuleProps(activeModule, rawData)} />;
  };

  return (
    <div className={classNames('right-panel-container', className)}>
      {(!outsideCall || (reportQueue && enableQueueReport) || aiReport) && (
        <div className="right-panel-header">
          <ModuleSwitcher
            onClose={() => {
              if (reportQueue) {
                setReportQueue(false);
                setActions({ ...actions, detailsPanel: false });
                return;
              }
              togglePanel();
            }}
            onExpand={handleExpand}
            isExpanded={isExpanded}
          />
        </div>
      )}

      <div className="right-panel-content">{renderActiveModule()}</div>
    </div>
  );
};

export default RightPanelContainer;
